import React, { useState, useEffect } from 'react';
import { roleAPI } from '../lib/api';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, Shield, Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getStatusColor } from '../lib/utils';

// Fallback modules if API fails (will be replaced by API data)
const DEFAULT_PERMISSION_MODULES = [
  { key: 'properties', label: 'Properties', category: 'Masters' },
  { key: 'stores', label: 'Stores', category: 'Masters' },
  { key: 'items', label: 'Items', category: 'Masters' },
  { key: 'categories', label: 'Categories', category: 'Masters' },
  { key: 'units', label: 'Units', category: 'Masters' },
  { key: 'vendors', label: 'Vendors', category: 'Masters' },
  { key: 'stock', label: 'Stock Operations', category: 'Stock' },
  { key: 'stock_inward', label: 'Stock Inward', category: 'Stock' },
  { key: 'stock_issue', label: 'Stock Issue', category: 'Stock' },
  { key: 'stock_transfer', label: 'Stock Transfer', category: 'Stock' },
  { key: 'purchase_orders', label: 'Purchase Orders', category: 'Procurement' },
  { key: 'grn', label: 'Goods Receipt (GRN)', category: 'Procurement' },
  { key: 'petty_cash', label: 'Petty Cash', category: 'Finance' },
  { key: 'payments', label: 'Payments', category: 'Finance' },
  { key: 'clients', label: 'Clients & Events', category: 'CRM' },
  { key: 'reports', label: 'Reports', category: 'Reports' },
  { key: 'users', label: 'Users', category: 'Administration' },
  { key: 'roles', label: 'Roles', category: 'Administration' },
  { key: 'settings', label: 'App Settings', category: 'Administration' }
];

const DEFAULT_PERMISSION_TYPES = ['view', 'create', 'edit', 'delete', 'approve', 'export', 'manage_masters'];

const defaultPermission = {
  view: false,
  create: false,
  edit: false,
  delete: false,
  approve: false,
  export: false,
  manage_masters: false
};

export const RolesPage = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: {},
    status: 'active'
  });
  const [saving, setSaving] = useState(false);
  
  // Dynamic permission modules from API
  const [permissionModules, setPermissionModules] = useState(DEFAULT_PERMISSION_MODULES);
  const [permissionTypes, setPermissionTypes] = useState(DEFAULT_PERMISSION_TYPES);
  // Fixed category order for consistent display
  const CATEGORY_ORDER = ['Masters', 'Stock', 'Procurement', 'Finance', 'CRM', 'Reports', 'Administration'];
  const [categories, setCategories] = useState(CATEGORY_ORDER);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rolesRes, modulesRes] = await Promise.all([
        roleAPI.getAll(),
        roleAPI.getPermissionModules().catch(() => null)
      ]);
      setRoles(rolesRes.data);
      
      // Update permission modules from API if available
      if (modulesRes?.data) {
        setPermissionModules(modulesRes.data.modules || DEFAULT_PERMISSION_MODULES);
        setPermissionTypes(modulesRes.data.permission_types || DEFAULT_PERMISSION_TYPES);
        // Keep fixed category order, but add any new categories at the end
        const apiCategories = modulesRes.data.categories || [];
        const newCategories = apiCategories.filter(c => !CATEGORY_ORDER.includes(c));
        setCategories([...CATEGORY_ORDER, ...newCategories]);
      }
    } catch (error) {
      toast.error('Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const initializePermissions = (existingPermissions = {}) => {
    const permissions = {};
    permissionModules.forEach(module => {
      permissions[module.key] = {
        ...defaultPermission,
        ...(existingPermissions[module.key] || {})
      };
    });
    return permissions;
  };

  const handleOpenDialog = (role = null) => {
    if (role) {
      setSelectedRole(role);
      setFormData({
        name: role.name || '',
        description: role.description || '',
        permissions: initializePermissions(role.permissions),
        status: role.status || 'active'
      });
    } else {
      setSelectedRole(null);
      setFormData({
        name: '',
        description: '',
        permissions: initializePermissions(),
        status: 'active'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRole(null);
  };

  const handlePermissionChange = (moduleKey, permissionType, checked) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [moduleKey]: {
          ...formData.permissions[moduleKey],
          [permissionType]: checked
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (selectedRole) {
        await roleAPI.update(selectedRole.id, formData);
        toast.success('Role updated successfully');
      } else {
        await roleAPI.create(formData);
        toast.success('Role created successfully');
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await roleAPI.delete(selectedRole.id);
      toast.success('Role deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedRole(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  const filteredRoles = roles.filter(r => 
    r.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Roles</h1>
          <p className="text-muted-foreground mt-1">Manage roles and permissions</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="rounded-full" data-testid="add-role-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Role
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search roles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-roles-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner h-8 w-8" />
            </div>
          ) : filteredRoles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Shield className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium text-foreground">No roles found</p>
              <p className="text-muted-foreground">Create roles to manage user permissions</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRoles.map((role) => (
                    <TableRow key={role.id} data-testid={`role-row-${role.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-primary" />
                          {role.name}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[300px] truncate">{role.description || '-'}</TableCell>
                      <TableCell>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(role.status)}`}>
                          {role.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(role)}
                            data-testid={`edit-role-${role.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedRole(role);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-role-${role.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedRole ? 'Edit Role' : 'Add Role'}</DialogTitle>
            <DialogDescription>
              {selectedRole ? 'Update role and permissions' : 'Create a new role with specific permissions'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Role Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="role-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="role-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  data-testid="role-description-input"
                />
              </div>

              {/* Permissions Matrix */}
              <div className="space-y-2">
                <Label>Permissions</Label>
                <div className="border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background">
                      <TableRow>
                        <TableHead className="w-[180px]">Module</TableHead>
                        <TableHead className="text-center w-[60px]">View</TableHead>
                        <TableHead className="text-center w-[60px]">Create</TableHead>
                        <TableHead className="text-center w-[60px]">Edit</TableHead>
                        <TableHead className="text-center w-[60px]">Delete</TableHead>
                        <TableHead className="text-center w-[60px]">Approve</TableHead>
                        <TableHead className="text-center w-[60px]">Export</TableHead>
                        <TableHead className="text-center w-[60px]">Manage</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* Group modules by category */}
                      {categories.map(category => {
                        const categoryModules = permissionModules.filter(m => m.category === category);
                        if (categoryModules.length === 0) return null;
                        return (
                          <React.Fragment key={category}>
                            <TableRow className="bg-muted/50">
                              <TableCell colSpan={8} className="font-semibold text-xs uppercase text-muted-foreground py-2">
                                {category}
                              </TableCell>
                            </TableRow>
                            {categoryModules.map((module) => (
                              <TableRow key={module.key}>
                                <TableCell className="font-medium text-sm">{module.label}</TableCell>
                                {permissionTypes.map((perm) => (
                                  <TableCell key={perm} className="text-center">
                                    <Checkbox
                                      checked={formData.permissions[module.key]?.[perm] || false}
                                      onCheckedChange={(checked) => handlePermissionChange(module.key, perm, checked)}
                                      data-testid={`perm-${module.key}-${perm}`}
                                    />
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                {/* Quick Actions */}
                <div className="flex gap-2 mt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const allPerms = {};
                      permissionModules.forEach(m => {
                        allPerms[m.key] = {};
                        permissionTypes.forEach(p => allPerms[m.key][p] = true);
                      });
                      setFormData({ ...formData, permissions: allPerms });
                    }}
                  >
                    Grant All
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const viewOnly = {};
                      permissionModules.forEach(m => {
                        viewOnly[m.key] = { view: true };
                      });
                      setFormData({ ...formData, permissions: viewOnly });
                    }}
                  >
                    View Only
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setFormData({ ...formData, permissions: initializePermissions() })}
                  >
                    Clear All
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-role-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {selectedRole ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedRole?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-role-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolesPage;
